import { Action } from "types/generated";

import { PermissionMetadata } from "./types";

export const PERMISSIONS: Record<Action, PermissionMetadata> = {
  // V1 actions
  [Action.SpaceAdmin]: {
    name: "Admin",
    subject: "Space",
    description: "Full administrative access to a space",
  },
  [Action.SpaceRead]: {
    name: "Read",
    subject: "Space",
    description:
      "Grants visibility into stacks, runs, and other resources within an assigned space",
    tooltipInfo:
      'Needed to access space-scoped resources unless custom role is combined with the system "Space Read" role.',
    tooltipInfoVariant: "standard" as const,
  },
  [Action.SpaceWrite]: {
    name: "Write",
    subject: "Space",
    description: "Write access to space resources",
  },
  [Action.SpaceShareModule]: {
    name: "Share module",
    subject: "Space",
    description: "Allow modules to be shared with an assigned space from other spaces",
  },
  [Action.AzureIntegrationConfirmConsent]: {
    name: "Confirm Azure integration consent",
    subject: "Integrations",
    description:
      "Record admin consent granted externally for Azure integrations. Does not grant or verify Azure permissions",
  },
  [Action.AzureIntegrationClearConsent]: {
    name: "Clear Azure integration consent confirmation",
    subject: "Integrations",
    description:
      "Clear Spacelift consent confirmation for Azure integrations without revoking Azure permissions",
  },
  [Action.RunTrigger]: {
    name: "Trigger run",
    subject: "Run",
    description: "Trigger stack runs in a given space",
  },
  [Action.RunTriggerWithCustomRuntimeConfig]: {
    name: "Trigger run with custom runtime config",
    subject: "Run",
    description: "Trigger stack runs in a given space with custom runtime config",
  },
  [Action.RunConfirm]: {
    name: "Confirm run",
    subject: "Run",
    description: "Confirm a plan and apply changes",
  },
  [Action.RunDiscard]: {
    name: "Discard run",
    subject: "Run",
    description: "Discard planned changes",
  },
  [Action.RunReview]: {
    name: "Review run",
    subject: "Run",
    description:
      "Review the planned changes and submit a review (approval/rejection). Does not replace the approval policy",
  },
  [Action.TaskCreate]: {
    name: "Create task",
    subject: "Task",
    description: "Create tasks for stacks in a given space",
  },
  [Action.StackManage]: {
    name: "Manage stack",
    subject: "Stack",
    description: "Manage stacks in a given space",
    tooltipInfo:
      'Covers everything "Update stack" does, plus creating, deleting and moving stacks, attaching contexts, policies and cloud integrations, and managing stack webhooks and role bindings. Required by the stack creation wizard.',
    tooltipInfoVariant: "standard" as const,
  },
  [Action.StackUpdate]: {
    name: "Update stack",
    subject: "Stack",
    description:
      "Update stack settings (details, source code, vendor, behavior) and run hooks in a given space",
    tooltipInfo:
      'Limited to the stack settings and run hooks. Creating, deleting or moving a stack, and attaching contexts, policies or cloud integrations, all require "Manage stack".',
    tooltipInfoVariant: "standard" as const,
  },
  [Action.StackDelete]: {
    name: "Delete stack",
    subject: "Stack",
    description: "Delete stacks in a given space",
  },
  [Action.StackLock]: {
    name: "Lock stack",
    subject: "Stack",
    description: "Locks a stack for exclusive use and prevents operations by other users",
  },
  [Action.StackUnlock]: {
    name: "Unlock stack",
    subject: "Stack",
    description: "Unlocks a previously locked stack, only if locked by the same user",
  },
  [Action.StackUnlockForce]: {
    name: "Force unlock stack",
    subject: "Stack",
    description: "Unlocks any locked stack, including those locked by other users",
  },

  [Action.StackAddConfig]: {
    name: "Add stack config",
    subject: "Stack",
    description: "Adds or updates stack environment variables and mounted files",
  },
  [Action.StackDeleteConfig]: {
    name: "Delete stack config",
    subject: "Stack",
    description: "Deletes stack environment variables and mounted files",
  },
  [Action.StackDisable]: {
    name: "Disable stack",
    subject: "Stack",
    description: "Disable a stack",
  },
  [Action.StackEnable]: {
    name: "Enable stack",
    subject: "Stack",
    description: "Enable a disabled stack",
  },
  [Action.ContextCreate]: {
    name: "Create context",
    subject: "Context",
    description: "Create contexts in a given space",
  },
  [Action.ContextUpdate]: {
    name: "Update context",
    subject: "Context",
    description: "Update contexts in a given space",
  },
  [Action.ContextDelete]: {
    name: "Delete context",
    subject: "Context",
    description: "Delete contexts in a given space",
  },
  [Action.WorkerDrainSet]: {
    name: "Drain worker",
    subject: "Worker Pool",
    description: "Drain or undrain workers in a given space",
  },
  [Action.WorkerPoolDelete]: {
    name: "Delete worker pool",
    subject: "Worker Pool",
    description: "Delete worker pools in a given space",
  },
  [Action.WorkerPoolReset]: {
    name: "Reset worker pool",
    subject: "Worker Pool",
    description: "Reset worker pools in a given space",
  },
  [Action.WorkerPoolUpdate]: {
    name: "Update worker pool",
    subject: "Worker Pool",
    description: "Update worker pools in a given space",
  },
  [Action.WorkerPoolCreate]: {
    name: "Create worker pool",
    subject: "Worker Pool",
    description: "Create worker pools in a given space",
  },
  [Action.WorkerPoolCycle]: {
    name: "Cycle worker pool",
    subject: "Worker Pool",
    description: "Cycle worker pools in a given space",
  },
  [Action.StackReslug]: {
    name: "Re-slug stack",
    subject: "Stack",
    description: "Re-slug stacks in a given space",
  },
  [Action.StackStateDownload]: {
    name: "Download Stack State",
    subject: "Stack",
    description: "Download the state file for a stack",
  },
  [Action.StackStateRead]: {
    name: "Read Stack State",
    subject: "Stack",
    description: "Read the state file for a stack",
  },
  [Action.StackUploadLocalWorkspace]: {
    name: "Upload local workspace for stack",
    subject: "Stack",
    description: "Generate upload URLs for local previews",
  },
  [Action.StackWorkspaceLock]: {
    name: "Lock stack workspace",
    subject: "Stack",
    description: "Lock the workspace for a stack",
  },
  [Action.StackWorkspaceUnlock]: {
    name: "Unlock stack workspace",
    subject: "Stack",
    description: "Unlock the workspace for a stack",
  },
  [Action.StackSetCurrentCommit]: {
    name: "Set stack current commit",
    subject: "Stack",
    description: "Set stack current commit in a given space",
  },
  [Action.StackSyncCommit]: {
    name: "Sync stack commit",
    subject: "Stack",
    description: "Sync the tracked branch head commit with the head commit of the stack",
  },
  [Action.RunCancel]: {
    name: "Cancel run",
    subject: "Run",
    description: "Cancel runs in a given space",
  },
  [Action.RunCancelBlocking]: {
    name: "Cancel blocking run",
    subject: "Run",
    description: "Cancel blocking runs in a given space",
  },
  [Action.RunComment]: {
    name: "Comment on run",
    subject: "Run",
    description: "Comment on runs in a given space",
  },
  [Action.RunRetry]: {
    name: "Retry run",
    subject: "Run",
    description: "Retry runs in a given space",
  },
  [Action.RunRetryBlocking]: {
    name: "Retry blocking run",
    subject: "Run",
    description: "Retry blocking runs in a given space",
  },
  [Action.RunStop]: {
    name: "Stop run",
    subject: "Run",
    description: "Stop runs in a given space",
  },
  [Action.RunStopBlocking]: {
    name: "Stop blocking run",
    subject: "Run",
    description: "Stop blocking runs in a given space",
  },
  [Action.RunKill]: {
    name: "Force kill run",
    subject: "Run",
    description: "Force kill runs in a given space",
  },
  [Action.RunKillBlocking]: {
    name: "Force kill blocking run",
    subject: "Run",
    description: "Force kill blocking runs in a given space",
  },
  [Action.RunPromote]: {
    name: "Promote run",
    subject: "Run",
    description: "Trigger a tracked run for the same Git commit as a proposed run",
  },
  [Action.RunProposeLocalWorkspace]: {
    name: "Propose run from local workspace",
    subject: "Run",
    description: "Trigger a proposed run based on local workspace",
  },
  [Action.RunProposeWithOverrides]: {
    name: "Propose run with overrides",
    subject: "Run",
    description: "Trigger a proposed run with environment variable overrides",
  },
  [Action.RunTargetedReplan]: {
    name: "Replan targeted run",
    subject: "Run",
    description: "Replan a targeted run in a given space",
  },
  [Action.RunPrioritizeSet]: {
    name: "Prioritize run",
    subject: "Run",
    description: "Prioritize a run in a given space",
  },

  // Actions for upcoming features
  [Action.StackCreate]: {
    name: "Create stack",
    subject: "Stack",
    description: "Create stacks in a given space",
  },
  [Action.StackManagedStateImport]: {
    name: "Import stack managed state",
    subject: "Stack",
    description: "Import managed state into a stack",
  },
  [Action.StackSetStar]: {
    name: "Star stack",
    subject: "Stack",
    description: "Star or unstar a stack",
  },
  [Action.ModuleCreate]: {
    name: "Create module",
    subject: "Module",
    description: "Create a module",
  },
  [Action.ModuleDisable]: {
    name: "Disable module",
    subject: "Module",
    description: "Disable a module",
  },
  [Action.ModuleTriggerVersion]: {
    name: "Trigger module version",
    subject: "Module",
    description: "Trigger a module version",
  },
  [Action.ModuleUploadLocalWorkspace]: {
    name: "Upload local workspace for module",
    subject: "Module",
    description: "Generate upload URLs for local previews",
  },
  [Action.ModuleProposeLocalWorkspace]: {
    name: "Propose local workspace for module",
    subject: "Module",
    description: "Propose a module version based on a local workspace",
  },
  [Action.ModuleEnable]: {
    name: "Enable module",
    subject: "Module",
    description: "Enable a module",
  },
  [Action.ModulePublish]: {
    name: "Publish module",
    subject: "Module",
    description: "Publish a module",
  },
  [Action.ModuleMarkAsBad]: {
    name: "Mark module as bad",
    subject: "Module",
    description: "Mark a module as bad",
  },
  [Action.ModuleVersionDelete]: {
    name: "Delete module version",
    subject: "Module",
    description: "Delete a module version",
  },
  [Action.StackManagedStateRollback]: {
    name: "Rollback stack managed state",
    subject: "Stack",
    description: "Rollback managed state for a stack",
  },
  [Action.RevisionCreate]: {
    name: "Create revision",
    subject: "Revision",
    description: "Create a revision",
  },
  [Action.RepoCreate]: {
    name: "Create repo",
    subject: "Repo",
    description: "Create repo",
  },
  [Action.RepoDelete]: {
    name: "Delete repo",
    subject: "Repo",
    description: "Delete repo",
  },
  [Action.RepoUpdate]: {
    name: "Update repo",
    subject: "Repo",
    description: "Update repo",
  },

  // Terraform Provider actions
  [Action.TerraformProviderCreate]: {
    name: "Create Terraform provider",
    subject: "Terraform Provider",
    description: "Create Terraform provider in a given space",
  },
  [Action.TerraformProviderSetVisibility]: {
    name: "Set Terraform provider visibility",
    subject: "Terraform Provider",
    description: "Set visibility for Terraform providers",
  },
  [Action.TerraformProviderUpdate]: {
    name: "Update Terraform provider",
    subject: "Terraform Provider",
    description: "Update Terraform provider in a given space",
  },
  [Action.TerraformProviderDelete]: {
    name: "Delete Terraform provider",
    subject: "Terraform Provider",
    description: "Delete Terraform provider in a given space",
  },
  [Action.TerraformProviderVersionCreate]: {
    name: "Create Terraform provider version",
    subject: "Terraform Provider",
    description: "Create Terraform provider version in a given space",
  },
  [Action.TerraformProviderVersionDelete]: {
    name: "Delete Terraform provider version",
    subject: "Terraform Provider",
    description: "Delete Terraform provider version in a given space",
  },
  [Action.TerraformProviderVersionPublish]: {
    name: "Publish Terraform provider version",
    subject: "Terraform Provider",
    description: "Publish Terraform provider version in a given space",
  },
  [Action.TerraformProviderVersionRegisterPlatform]: {
    name: "Register Terraform provider version platform",
    subject: "Terraform Provider",
    description: "Register platform for Terraform provider versions",
  },
  [Action.TerraformProviderVersionRevoke]: {
    name: "Revoke Terraform provider version",
    subject: "Terraform Provider",
    description: "Revoke Terraform provider version in a given space",
  },
  [Action.TerraformProviderVersionUpdate]: {
    name: "Update Terraform provider version",
    subject: "Terraform Provider",
    description: "Update Terraform provider version in a given space",
  },

  // Intent Project actions
  [Action.IntentProjectCloudIntegrationAttach]: {
    name: "Attach AWS integration to intent project",
    subject: "Intent",
    description: "Attach AWS integration to an intent project",
  },
  [Action.IntentProjectCloudIntegrationDetach]: {
    name: "Detach AWS integration from intent project",
    subject: "Intent",
    description: "Detach AWS integration from an intent project",
  },
  [Action.IntentProjectConfigAdd]: {
    name: "Add intent project config",
    subject: "Intent",
    description: "Add configuration to an intent project",
  },
  [Action.IntentProjectConfigDelete]: {
    name: "Delete intent project config",
    subject: "Intent",
    description: "Delete configuration from an intent project",
  },
  [Action.IntentProjectConfigUpdate]: {
    name: "Update intent project config",
    subject: "Intent",
    description: "Update configuration in an intent project",
  },
  [Action.IntentProjectCreate]: {
    name: "Create intent project",
    subject: "Intent",
    description: "Create a new intent project",
  },
  [Action.IntentProjectDelete]: {
    name: "Delete intent project",
    subject: "Intent",
    description: "Delete an intent project",
  },
  [Action.IntentProjectDisable]: {
    name: "Disable intent project",
    subject: "Intent",
    description: "Disable an intent project",
  },
  [Action.IntentProjectEnable]: {
    name: "Enable intent project",
    subject: "Intent",
    description: "Enable an intent project",
  },
  [Action.IntentProjectLock]: {
    name: "Lock intent project",
    subject: "Intent",
    description: "Lock an intent project",
  },
  [Action.IntentProjectPolicyAttach]: {
    name: "Attach policy to intent project",
    subject: "Intent",
    description: "Attach a policy to an intent project",
  },
  [Action.IntentProjectPolicyDetach]: {
    name: "Detach policy from intent project",
    subject: "Intent",
    description: "Detach a policy from an intent project",
  },
  [Action.IntentProjectUnlock]: {
    name: "Unlock intent project",
    subject: "Intent",
    description: "Unlock an intent project",
  },
  [Action.IntentProjectUpdate]: {
    name: "Update intent project",
    subject: "Intent",
    description: "Update an intent project",
  },
  [Action.IntentResourceOperationReview]: {
    name: "Review intent resource operation",
    subject: "Intent",
    description: "Review an intent resource operation",
  },
  [Action.IntentResourceCreate]: {
    name: "Create intent resources",
    subject: "Intent",
    description: "Create new cloud resources",
  },
  [Action.IntentResourceUpdate]: {
    name: "Update intent resources",
    subject: "Intent",
    description: "Update existing cloud resources",
  },
  [Action.IntentResourceDelete]: {
    name: "Delete intent resources",
    subject: "Intent",
    description: "Delete cloud resources",
  },
  [Action.IntentResourceImport]: {
    name: "Import intent resources",
    subject: "Intent",
    description: "Import existing cloud resources into state",
  },
  [Action.IntentResourceRefresh]: {
    name: "Refresh intent resources",
    subject: "Intent",
    description: "Refresh cloud resource state",
  },
  [Action.IntentResourceResume]: {
    name: "Resume intent resources",
    subject: "Intent",
    description: "Resume intent resource operations",
  },
  [Action.IntentStateRead]: {
    name: "Read intent state",
    subject: "Intent",
    description: "Read intent state details",
  },
  [Action.IntentStateEject]: {
    name: "Eject from intent state",
    subject: "Intent",
    description: "Eject resources from state",
  },
  [Action.IntentPolicyCreate]: {
    name: "Create intent policies",
    subject: "Intent",
    description: "Create new intent policies",
  },
  [Action.IntentPolicyUpdate]: {
    name: "Update intent policies",
    subject: "Intent",
    description: "Update existing intent policies",
  },
  [Action.IntentPolicyDelete]: {
    name: "Delete intent policies",
    subject: "Intent",
    description: "Delete intent policies",
  },
  [Action.IntentDependenciesAdd]: {
    name: "Add intent dependencies",
    subject: "Intent",
    description: "Add dependencies to intent project",
  },
  [Action.IntentDependenciesRemove]: {
    name: "Remove intent dependencies",
    subject: "Intent",
    description: "Remove dependencies from intent project",
  },
  [Action.AiIntegrationCreate]: {
    name: "Create AI integration",
    subject: "Intent",
    description: "Create AI integrations in a given space",
  },
  [Action.AiIntegrationUpdate]: {
    name: "Update AI integration",
    subject: "Intent",
    description: "Update AI integrations in a given space",
  },
  [Action.AiIntegrationToggle]: {
    name: "Enable or disable AI integration",
    subject: "Intent",
    description: "Enable or disable AI integrations in a given space",
  },
  [Action.AiIntegrationDelete]: {
    name: "Delete AI integration",
    subject: "Intent",
    description: "Delete AI integrations in a given space",
  },

  // Templates actions
  [Action.CreateBlueprintsVersionedGroup]: {
    name: "Create template",
    subject: "Templates",
    description: "Create a template",
  },
  [Action.DeleteBlueprintsVersionedGroup]: {
    name: "Delete template",
    subject: "Templates",
    description: "Delete a template",
  },
  [Action.UpdateBlueprintsVersionedGroup]: {
    name: "Update template",
    subject: "Templates",
    description: "Update a template",
  },
  [Action.UpgradeBlueprintDeployment]: {
    name: "Upgrade deployment version",
    subject: "Templates",
    description: "Upgrade a template deployment version",
  },
  [Action.UpdateBlueprintDeployment]: {
    name: "Update deployment metadata",
    subject: "Templates",
    description: "Update a template deployment metadata",
  },
  [Action.UpdateBlueprintDeploymentInputs]: {
    name: "Update deployment inputs",
    subject: "Templates",
    description: "Update inputs for a template deployment",
  },
  [Action.CreateBlueprintDeployment]: {
    name: "Create template deployment",
    subject: "Templates",
    description: "Create a deployment from a template",
  },
  [Action.DeleteBlueprintDeployment]: {
    name: "Delete template deployment",
    subject: "Templates",
    description: "Delete a template deployment",
  },
  [Action.RollbackBlueprintDeployment]: {
    name: "Rollback template deployment",
    subject: "Templates",
    description: "Rollback a template deployment to a previous version",
  },
  [Action.TestBlueprintDeployment]: {
    name: "Test template deployment",
    subject: "Templates",
    description: "Test a template deployment",
  },

  // Drift Detection Integration actions
  [Action.DriftDetectionIntegrationCreate]: {
    name: "Create drift detection integration",
    subject: "Drift Detection",
    description: "Create drift detection for a stack",
  },
  [Action.DriftDetectionIntegrationUpdate]: {
    name: "Update drift detection integration",
    subject: "Drift Detection",
    description: "Update drift detection for a stack",
  },
  [Action.DriftDetectionIntegrationDelete]: {
    name: "Delete drift detection integration",
    subject: "Drift Detection",
    description: "Delete drift detection for a stack",
  },

  // Stack Scheduled actions
  [Action.StackScheduledDeleteCreate]: {
    name: "Create scheduled stack delete",
    subject: "Stack",
    description: "Create a scheduled delete for a stack",
  },
  [Action.StackScheduledDeleteDelete]: {
    name: "Delete scheduled stack delete",
    subject: "Stack",
    description: "Delete a scheduled delete for a stack",
  },
  [Action.StackScheduledDeleteUpdate]: {
    name: "Update scheduled stack delete",
    subject: "Stack",
    description: "Update a scheduled delete for a stack",
  },
  [Action.StackScheduledTaskCreate]: {
    name: "Create scheduled stack task",
    subject: "Stack",
    description: "Create a scheduled task for a stack",
  },
  [Action.StackScheduledTaskDelete]: {
    name: "Delete scheduled stack task",
    subject: "Stack",
    description: "Delete a scheduled task for a stack",
  },
  [Action.StackScheduledTaskUpdate]: {
    name: "Update scheduled stack task",
    subject: "Stack",
    description: "Update a scheduled task for a stack",
  },
  [Action.StackScheduledRunCreate]: {
    name: "Create scheduled stack run",
    subject: "Stack",
    description: "Create a scheduled run for a stack",
  },
  [Action.StackScheduledRunDelete]: {
    name: "Delete scheduled stack run",
    subject: "Stack",
    description: "Delete a scheduled run for a stack",
  },
  [Action.StackScheduledRunUpdate]: {
    name: "Update scheduled stack run",
    subject: "Stack",
    description: "Update a scheduled run for a stack",
  },

  // Stack action decomposition (replaces StackManage)
  [Action.StackContextAttach]: {
    name: "Attach context to stack",
    subject: "Stack",
    description: "Attach contexts to stacks",
  },
  [Action.StackContextDetach]: {
    name: "Detach context from stack",
    subject: "Stack",
    description: "Detach contexts from stacks",
  },
  [Action.StackPolicyAttach]: {
    name: "Attach policy to stack",
    subject: "Stack",
    description: "Attach policies to stacks",
  },
  [Action.StackPolicyDetach]: {
    name: "Detach policy from stack",
    subject: "Stack",
    description: "Detach policies from stacks",
  },
  [Action.StackAwsIntegrationAttach]: {
    name: "Attach AWS integration to stack",
    subject: "Stack",
    description: "Attach AWS integrations to stacks",
  },
  [Action.StackAwsIntegrationDetach]: {
    name: "Detach AWS integration from stack",
    subject: "Stack",
    description: "Detach AWS integrations from stacks",
  },
  [Action.StackAwsIntegrationUpdate]: {
    name: "Update AWS integration on stack",
    subject: "Stack",
    description: "Update AWS integrations on stacks",
  },
  [Action.StackAzureIntegrationAttach]: {
    name: "Attach Azure integration to stack",
    subject: "Stack",
    description: "Attach Azure integrations to stacks",
  },
  [Action.StackAzureIntegrationDetach]: {
    name: "Detach Azure integration from stack",
    subject: "Stack",
    description: "Detach Azure integrations from stacks",
  },
  [Action.StackAzureIntegrationUpdate]: {
    name: "Update Azure integration on stack",
    subject: "Stack",
    description: "Update Azure integrations on stacks",
  },
  [Action.StackGcpIntegrationCreate]: {
    name: "Create GCP integration on stack",
    subject: "Stack",
    description: "Create GCP integrations for stacks",
  },
  [Action.StackGcpIntegrationDelete]: {
    name: "Delete GCP integration on stack",
    subject: "Stack",
    description: "Delete GCP integrations from stacks",
  },
  [Action.StackWebhookCreate]: {
    name: "Create stack webhook",
    subject: "Stack",
    description: "Create webhooks for stacks",
  },
  [Action.StackWebhookUpdate]: {
    name: "Update stack webhook",
    subject: "Stack",
    description: "Update webhooks for stacks",
  },
  [Action.StackWebhookDelete]: {
    name: "Delete stack webhook",
    subject: "Stack",
    description: "Delete webhooks for stacks",
  },
  [Action.StackRoleBindingCreate]: {
    name: "Create stack role binding",
    subject: "Stack",
    description: "Create role bindings for stacks",
  },
  [Action.StackRoleBindingUpdate]: {
    name: "Update stack role binding",
    subject: "Stack",
    description: "Update role bindings for stacks",
  },
  [Action.StackRoleBindingDelete]: {
    name: "Delete stack role binding",
    subject: "Stack",
    description: "Delete role bindings for stacks",
  },
};
